// /company-setup — TH hub for Thailand company formation (Phase P-COMPANY-SETUP)
import { RelatedHubGrid } from "@/components/RelatedHubGrid";
import { createFileRoute, Link } from "@tanstack/react-router";
import { consolidateGraph } from "@/lib/schema-graph";
import { speakableSchema, faqPageSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { TrustLayer } from "@/components/TrustLayer";
import { Phone, MessageCircle, ArrowRight, Sparkles, Building2, Briefcase, Globe, Scale } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

export const Route = createFileRoute("/company-setup/")({
    loader: async () => {
    const { COMPANY_ENTITIES } = await import("@/data/company-setup-types");
    return { __d: { COMPANY_ENTITIES } };
  },
head: ({ loaderData }) => {
    const { COMPANY_ENTITIES } = loaderData!.__d as typeof import("@/data/company-setup-types");
    const url = `${SITE}/company-setup`;
    const title = `จดทะเบียนบริษัท Thailand 2026 — BOI / FBL / Branch / Rep Office / IBC | NYC`;
    const desc = `ครบทุกรูปแบบนิติบุคคลในไทย — Thai Limited, BOI Promotion (CIT 3-8 yrs), Foreign Business License, Branch Office, Representative Office, IBC/ROH, US Treaty of Amity, Partnership พร้อม visa + work permit — ปรึกษาก่อนยื่นจริง ลดโอกาสเอกสารตีกลับ`;
    return {
      meta: [
        { title: ctaTitle(title, "/company-setup", "th") }, { name: "description", content: ctaDesc(desc, "/company-setup", "th")},
        { property: "og:title", content: ctaTitle(title, "/company-setup", "th")}, { property: "og:description", content: ctaDesc(desc, "/company-setup", "th")},
        { property: "og:url", content: url }, { property: "og:locale", content: "th_TH" },
        { name: "keywords", content: "จดทะเบียนบริษัทไทย, BOI Thailand, Foreign Business License, Branch Office Thailand, Representative Office, IBC Thailand, Treaty of Amity, Thai Limited Company, จดทะเบียนพาณิชย์ DBD" },
      ],
      links: [{ rel: "canonical", href: url }, ...hreflangLinks("/company-setup", "/en/company-setup")],
      scripts: [
        consolidateGraph([
          speakableSchema([".aeo-speakable", "h1", "h2"]),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "CollectionPage", "@id": `${url}#page`,
              name: title, description: desc, url, inLanguage: "th-TH",
              mainEntity: {
                "@type": "ItemList", numberOfItems: COMPANY_ENTITIES.length,
                itemListElement: COMPANY_ENTITIES.map((e, i) => ({
                  "@type": "ListItem", position: i + 1, name: e.titleTh, url: `${SITE}/company-setup/${e.slug}`,
                })),
              },
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org", "@type": "Service", "@id": `${url}#service`,
              name: "Thailand Company Formation Service",
              serviceType: "Company Registration & Corporate Structuring (DBD / BOI / FBL)",
              provider: { "@type": "LegalService", name: "NYC Legal & Notary Service", telephone: "+66922429293", url: SITE },
              areaServed: "TH",
              offers: { "@type": "Offer", price: "14000", priceCurrency: "THB", description: "เริ่มต้น 14,000 บาท: Partnership / Thai Limited end-to-end registration" },
            }),
          },
          faqPageSchema([
            { q: "จดทะเบียนบริษัทไทยต้องมีอะไรบ้าง?", a: "ผู้ถือหุ้นอย่างน้อย 2 คน, ทุนจดทะเบียน ≥ 15 บาท/หุ้น (แนะนำ 2 ล้านบาท/work permit), ที่อยู่จดทะเบียน, MOA, ประชุมจัดตั้ง — เสร็จภายใน 7 วันทำการ" },
            { q: "BOI vs FBL vs Treaty ใช้อะไร?", a: "BOI = สิทธิภาษี + 100% ต่างชาติในกิจการส่งเสริม · FBL = ใบอนุญาตเฉพาะธุรกิจสงวน · Treaty of Amity = สิทธิ 100% เฉพาะคนอเมริกัน — เลือกตาม profile ธุรกิจ" },
            { q: "Branch Office กับ Subsidiary ต่างกันอย่างไร?", a: "Branch = ไม่ใช่นิติบุคคลแยก บริษัทแม่รับผิดเต็ม + ต้องขอ FBL · Subsidiary (Thai Limited) = นิติบุคคลแยก รับผิดจำกัด ไม่ต้อง FBL ถ้าไทยถือ 51%+" },
            { q: "ต้องจ้างคนไทยกี่คนต่อ work permit?", a: "อัตราส่วน 4:1 (พนักงานไทย 4 คน ต่อชาวต่างชาติ 1 คน) ต่อ Work Permit 1 ใบ ตามกฎกระทรวงแรงงาน — บริษัทที่ได้รับการส่งเสริมจาก BOI, IEAT, IBC, Treaty of Amity หรือทำธุรกิจตัวแทนจัดหางาน/สำนักงานผู้แทน ได้รับยกเว้นอัตราส่วนนี้ และทุนจดทะเบียนต้องชำระแล้ว 2 ล้านบาทต่อชาวต่างชาติ 1 คน (1 ล้านบาทถ้าจดทะเบียนสมรสกับคนไทย)" },
            { q: "ค่าบริการ NYC เท่าไร?", a: "Partnership เริ่ม 14,000 / Thai Limited Company 19,000 / Treaty of Amity (US) 65,000 / Foreign Business License (FBL) 85,000 / BOI Promotion 95,000 / International Business Center (IBC) 150,000 บาท — รวมค่าจดทะเบียน DBD, ร่างหนังสือบริคณห์สนธิ, จัดประชุมผู้ถือหุ้น, ขอเลขประจำตัวผู้เสียภาษี และ VAT สอบถามรายละเอียดที่ LINE @nycli, 083-249-4999" },
          ]),
        ]),
      ],
    };
  },
  component: CompanyHubTh,
});

function CompanyHubTh() {
  const { COMPANY_ENTITIES } = (Route.useLoaderData() as { __d: typeof import("@/data/company-setup-types") }).__d;
  return (
    <main className="min-h-screen bg-background">
      <section className="bg-gradient-to-b from-primary/10 to-background px-4 pt-16 pb-12">
        <div className="mx-auto max-w-6xl">
          <nav aria-label="Breadcrumb" className="mb-4 text-xs text-muted-foreground">
            <Link to="/" className="hover:text-primary">หน้าแรก</Link><span className="mx-2">/</span><span>จดทะเบียนบริษัท</span>
          </nav>
          <span className="inline-block rounded-full bg-primary/15 px-3 py-1 text-xs font-bold text-primary">
            <Building2 className="mr-1 inline h-3 w-3" /> DBD · BOI · FBL · Treaty · 8 รูปแบบ
          </span>
          <h1 className="mt-3 text-3xl font-extrabold sm:text-5xl">จดทะเบียนบริษัทในไทย ครบทุกรูปแบบ</h1>
          <p className="mt-4 max-w-3xl text-lg text-muted-foreground">
            Thai Limited · BOI Promotion · Foreign Business License · Branch Office · Representative Office · International Business Center (IBC) · US Treaty of Amity · Partnership — เลือกโครงสร้างที่เหมาะกับธุรกิจคุณ พร้อมวีซ่า + work permit
          </p>
          <div className="quick-answer aeo-speakable mt-6 max-w-3xl rounded-2xl border border-primary/30 bg-primary/5 p-5" data-speakable="true">
            <div className="mb-2 inline-flex items-center gap-1.5 rounded-full bg-primary/15 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-widest text-primary">
              <Sparkles className="h-3 w-3" /> AI Quick Answer
            </div>
            <h2 className="text-base font-bold sm:text-lg">เลือกจดทะเบียนแบบไหนดี?</h2>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground sm:text-base">
              <strong>Thai Limited (49/51)</strong> = SMEs/ร้านอาหาร · <strong>BOI</strong> = ยกเว้น CIT 3-13 ปี + 100% ต่างชาติ (manufacturing, software, EV) · <strong>FBL</strong> = ต่างชาติทำธุรกิจสงวน · <strong>Branch</strong> = สาขาบริษัทแม่ ทุน 3M · <strong>Rep Office</strong> = สำรวจตลาด ไม่ทำรายได้ · <strong>IBC</strong> = ศูนย์กลางภูมิภาค CIT 3-8% · <strong>Treaty of Amity</strong> = คนอเมริกัน 100% · <strong>Partnership</strong> = ธุรกิจครอบครัวเล็ก
            </p>
          </div>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="tel:+66922429293" className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-3 text-sm font-bold text-primary-foreground hover:opacity-90">
              <Phone className="h-4 w-4" /> โทร 092-242-9293
            </a>
            <a href="https://line.me/R/ti/p/@nyc-notary" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <MessageCircle className="h-4 w-4" /> LINE @nyc-notary
            </a>
            <Link to="/work-permit" className="inline-flex items-center gap-2 rounded-lg border border-border bg-card px-5 py-3 text-sm font-bold hover:bg-muted">
              <Briefcase className="h-4 w-4" /> Work Permit
            </Link>
          </div>
        </div>
      </section>

      <section className="px-4 py-12">
        <div className="mx-auto max-w-6xl">
          <h2 className="text-2xl font-extrabold sm:text-3xl">8 รูปแบบนิติบุคคล</h2>
          <p className="mt-2 text-sm text-muted-foreground">เลือกตาม profile ธุรกิจ · คลิกเพื่อดูเงื่อนไข เอกสาร ขั้นตอน และค่าใช้จ่ายแบบละเอียด</p>
          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {COMPANY_ENTITIES.map((e) => (
              <Link
                key={e.slug}
                to="/company-setup/$entity"
                params={{ entity: e.slug }}
                className="group rounded-2xl border border-border bg-card p-5 transition hover:-translate-y-0.5 hover:border-primary hover:shadow-md"
                data-track-zone="company-hub-th"
              >
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{e.icon}</span>
                  <div className="font-bold text-sm">{e.titleTh}</div>
                </div>
                <div className="mt-2 line-clamp-2 text-xs text-muted-foreground">{e.taglineTh}</div>
                <div className="mt-3 text-[11px] font-semibold text-foreground/80">💰 {e.capitalTh}</div>
                <div className="mt-1 text-[11px] text-muted-foreground">🌐 {e.foreignOwnershipTh}</div>
                <div className="mt-3 flex items-center justify-between text-xs">
                  <span className="rounded-full bg-muted px-2 py-0.5 font-semibold">฿{e.servicePriceTHB.toLocaleString()}</span>
                  <span className="inline-flex items-center gap-1 font-semibold text-primary">
                    ดู <ArrowRight className="h-3 w-3" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-muted/30 px-4 py-10">
        <div className="mx-auto max-w-5xl">
          <h2 className="text-xl font-extrabold"><Scale className="mr-2 inline h-5 w-5 text-primary" />บริการที่เกี่ยวข้อง</h2>
          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[
              { t: "Work Permit", d: "วีซ่า Non-B + ใบอนุญาตทำงาน", to: "/work-permit" as const, icon: <Briefcase className="h-4 w-4 text-primary" /> },
              { t: "LTR Visa", d: "10 ปี · HSP 17% flat", to: "/ltr" as const, icon: <Globe className="h-4 w-4 text-primary" /> },
              { t: "DTV Visa", d: "5 ปี · Workcation/Freelancer", to: "/dtv" as const, icon: <Globe className="h-4 w-4 text-primary" /> },
              { t: "Visa Compliance", d: "TM30 / 90-day / Re-entry", to: "/visa-compliance" as const, icon: <Scale className="h-4 w-4 text-primary" /> },
              { t: "MFA + Apostille", d: "รับรองเอกสารบริษัทต่างประเทศ", to: "/mfa-legalisation" as const, icon: <Scale className="h-4 w-4 text-primary" /> },
              { t: "แปลเอกสารธุรกิจ", d: "MOA, FS, Resolution", to: "/translate" as const, icon: <Scale className="h-4 w-4 text-primary" /> },
            ].map((s) => (
              <Link key={s.t} to={s.to} className="rounded-xl border border-border bg-card p-4 hover:border-primary">
                <div className="flex items-center gap-2">{s.icon}<div className="font-bold">{s.t}</div></div>
                <div className="mt-1 text-xs text-muted-foreground">{s.d}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <TrustLayer variant="compact" />
    <RelatedHubGrid locale="th" /></main>
  );
}
